import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { withRetry } from '../lib/query'
import { db } from '../lib/db'
import { t, getLang } from '../lib/i18n'
import { buildCsv, downloadCsv, exportFilename, EXPORT_COLUMNS } from '../lib/csv'

/**
 * WHAT THE WORK ADDS UP TO
 * ------------------------
 * Until this screen existed, the platform only took data in. An officer walked
 * all day and the only thing that came back was a number going up. That is the
 * classic way a monitoring system loses the people who feed it: the field team
 * stops believing the work matters, because nothing they can see depends on it.
 *
 * TWO SHAPES, DELIBERATELY NOT ONE.
 *
 *   The report   — counts. No names, no coordinates. Safe to photograph, safe
 *                  to put in a slide, safe to send to a donor.
 *   The export   — one row per household, INCLUDING names. Personal data. The
 *                  database restricts it to supervisor and above; this screen
 *                  says so out loud rather than hiding the button, because a
 *                  missing button teaches people the app is broken.
 *
 * ONLINE, BUT NOT ONLY ONLINE. The figures come from the server, because
 * counting four hundred households against a denominator is the server's job.
 * But the last set is kept on the phone and shown with the time it was fetched,
 * so an officer standing in the village can still see where coverage stood this
 * morning. A screen that shows nothing without signal is a screen nobody opens.
 */

type Row = {
  section: string
  code: string
  label_en: string
  label_km: string
  value: number | null
  of_total: number | null
  sort_order: number
}

type Cached = { rows: Row[]; at: string }

const SECTION_KEY: Record<string, string> = {
  coverage: 'report.section.coverage',
  people: 'report.section.people',
  sanitation: 'report.section.sanitation',
  water: 'report.section.water',
  quality: 'report.section.quality',
}

/** Supervisor and above. The same list the database enforces in the view. */
const CAN_EXPORT = ['supervisor', 'merl_officer', 'admin']

export function ReportScreen({
  villageId,
  villageName,
  villageCode,
  roleCode,
  onBack,
}: {
  villageId: string
  villageName: string
  villageCode: string
  roleCode: string | null
  onBack: () => void
}) {
  const [rows, setRows] = useState<Row[]>([])
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportNote, setExportNote] = useState<string | null>(null)

  const cacheKey = `report:${villageId}`
  const mayExport = roleCode != null && CAN_EXPORT.includes(roleCode)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    // Whatever the phone last saw, on screen immediately.
    const cached = (await db.meta.get(cacheKey))?.value as Cached | undefined
    if (cached) {
      setRows(cached.rows)
      setFetchedAt(cached.at)
      setStale(true)
      setLoading(false)
    }

    const res = await withRetry(() => supabase.rpc('village_report', { p_village_id: villageId }))

    if (res.error) {
      // Only an error if there is nothing to show. Offline with this morning's
      // figures is a working state, not a failure.
      if (!cached) setError(res.error.message)
      setLoading(false)
      return
    }

    const fresh = (res.data ?? []) as Row[]
    const at = new Date().toISOString()
    setRows(fresh)
    setFetchedAt(at)
    setStale(false)
    setLoading(false)
    await db.meta.put({ key: cacheKey, value: { rows: fresh, at } as Cached })
  }, [villageId, cacheKey])

  useEffect(() => {
    load()
  }, [load])

  // ---------------------------------------------------------------------------
  //  THE EXPORT
  // ---------------------------------------------------------------------------
  //  Paged, not fetched in one go. PostgREST caps a request at 1000 rows by
  //  default and returns the first thousand WITHOUT an error — so a village of
  //  1200 households would export 1000 of them and look complete. Asking until
  //  a short page comes back is the only way to know the file is whole.
  async function exportCsv() {
    setExporting(true)
    setExportNote(null)
    setError(null)

    const PAGE = 500
    const all: Record<string, unknown>[] = []

    try {
      for (let from = 0; ; from += PAGE) {
        const res = await withRetry(() =>
          supabase
            .from('v_export_households')
            .select('*')
            .eq('village_id', villageId)
            .order('household_code', { nullsFirst: false })
            .range(from, from + PAGE - 1),
        )
        if (res.error) throw new Error(res.error.message)
        const page = (res.data ?? []) as Record<string, unknown>[]
        all.push(...page)
        if (page.length < PAGE) break
      }

      if (all.length === 0) {
        // Zero rows here is not "no data" — the view returns nothing to anyone
        // below supervisor, and to a supervisor with no assignment. Saying
        // "empty file" would be a lie in both cases.
        setExportNote(t('report.exportNone'))
        return
      }

      downloadCsv(exportFilename(villageCode), buildCsv(all, EXPORT_COLUMNS))
      setExportNote(`${all.length} ${t('report.exportDone')}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setExporting(false)
    }
  }

  const km = getLang() === 'km'
  const sections = [...new Set(rows.map((r) => r.section))]

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('report.title')}</h1>
        <button className="btn-quiet" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="content">
        <p className="muted" style={{ marginTop: 0 }}>
          {villageName} · {villageCode}
        </p>

        {/* WHEN, not just WHAT. A number with no date is a number somebody will
            quote next month believing it is current. */}
        {fetchedAt && (
          <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
            {stale ? t('report.asOfOffline') : t('report.asOf')}{' '}
            {new Date(fetchedAt).toLocaleString()}
          </p>
        )}

        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}

        {loading && rows.length === 0 && <p className="spinner">{t('common.loading')}</p>}

        {sections.map((s) => (
          <div key={s} style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>{t(SECTION_KEY[s] ?? s)}</h2>
            <ul className="list">
              {rows
                .filter((r) => r.section === s)
                .map((r) => {
                  const pct =
                    r.of_total != null && r.of_total > 0 && r.value != null
                      ? Math.round((1000 * r.value) / r.of_total) / 10
                      : null
                  return (
                    <li key={`${r.section}.${r.code}`} style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>{km ? r.label_km : r.label_en}</span>
                        <strong style={{ whiteSpace: 'nowrap' }}>
                          {/* A blank is not a zero. "Average household size"
                              with no households is unknown, not 0.0, and
                              printing 0 there invites someone to report it. */}
                          {r.value == null ? t('common.notRecorded') : r.value}
                          {r.code === 'pct' && r.value != null && '%'}
                        </strong>
                      </div>
                      {pct != null && r.code !== 'pct' && (
                        <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
                          {pct}% {t('report.ofRegistered')}
                        </div>
                      )}
                    </li>
                  )
                })}
            </ul>
          </div>
        ))}

        {/* ---- the export ---------------------------------------------------- */}
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>{t('report.exportHeading')}</h2>

          {mayExport ? (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                {t('report.exportWarning')}
              </p>
              <button className="btn-primary" onClick={exportCsv} disabled={exporting}>
                {exporting ? t('report.exporting') : t('report.exportCsv')}
              </button>
            </>
          ) : (
            /* Explained, not hidden. Somebody who is told why can ask for the
               access they need; somebody shown a missing button files a bug. */
            <p className="muted" style={{ marginTop: 0 }}>{t('report.exportDenied')}</p>
          )}

          {exportNote && (
            <div className="alert" style={{ marginTop: 12 }} role="status">
              {exportNote}
            </div>
          )}
        </div>

        <p className="muted" style={{ marginTop: 28, fontSize: 14 }}>
          {t('report.shareNote')}
        </p>
      </div>
    </div>
  )
}
